// src/pages/CalendarPage/CalendarPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getAllEvents } from "./../../services/eventServices";
import EventCardUser from "./EventCardUser";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import EventDetailsModal from "./EventDetailsModal";
import EventTableUser from "./EventTableUser";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import { FaTh, FaList, FaSearch, FaFilter, FaUndo } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const CalendarPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [statusFilter, setStatusFilter] = useState("A Venir"); // "Tous" | "A Venir" | "Passé"
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "table"

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getAllEvents();
        const activeEvents = (data || [])
          .filter((event) => event.is_active)
          .sort(
            (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
          );
        setEvents(activeEvents);
      } catch (error) {
        console.error("Erreur lors du chargement des événements", error);
      }
    };

    fetchEvents();
  }, []);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const typeEvents = [
    "Compétition",
    "Animation",
    "Stage & Regroupement Technique",
    "Passage de Grade",
    "Entraînement Départemental",
    "Stage Sportif Départemental",
    "Tournoi Interclubs",
    "Cours commun Club",
    "Stage Club",
    "Autre",
  ];

  const levelEvents = [
    "Club",
    "Départemental",
    "Régional",
    "National",
    "International",
  ];

  const cateEvents = [
    "Eveil",
    "Mini-Poussin(e)",
    "Poussin(e)",
    "Benjamin(e)",
    "Minime",
    "Cadet(te)",
    "Junior",
    "Sénior",
    "Vétéran",
  ];

  const statusOptions = ["Tous", "A Venir", "Passé"];

  const toggleFilterValue = (value, selected, setSelected) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleCategory = (cate) =>
    toggleFilterValue(cate, selectedCategories, setSelectedCategories);
  const toggleType = (type) =>
    toggleFilterValue(type, selectedTypes, setSelectedTypes);
  const toggleLevel = (level) =>
    toggleFilterValue(level, selectedLevels, setSelectedLevels);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedTypes([]);
    setSelectedLevels([]);
    setStatusFilter("A Venir");
  };

  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return events.filter((event) => {
      const title = (event.titre || "").toLowerCase();
      const search = searchTerm.toLowerCase();

      const eventStartDate = new Date(event.start);
      eventStartDate.setHours(0, 0, 0, 0);

      const isPast = eventStartDate < today;
      const isTodayOrFuture = eventStartDate >= today;

      let matchesStatus = true;
      if (statusFilter === "A Venir") matchesStatus = isTodayOrFuture;
      else if (statusFilter === "Passé") matchesStatus = isPast;
      else matchesStatus = true; // "Tous"

      const categories = event.cate_event
        ? event.cate_event.split(",").map((c) => c.trim())
        : [];

      const matchesCategories =
        selectedCategories.length === 0
          ? true
          : categories.some((cate) => selectedCategories.includes(cate));

      const matchesTypes =
        selectedTypes.length === 0 ||
        (event.type_event && selectedTypes.includes(event.type_event));

      const matchesLevels =
        selectedLevels.length === 0 ||
        (event.level_event && selectedLevels.includes(event.level_event));

      const matchesSearch = title.includes(search);

      return (
        matchesStatus &&
        matchesCategories &&
        matchesTypes &&
        matchesLevels &&
        matchesSearch
      );
    });
  }, [
    events,
    searchTerm,
    selectedCategories,
    selectedTypes,
    selectedLevels,
    statusFilter,
  ]);

  const chipBase =
    "px-3 py-1.5 rounded-full text-[11px] md:text-xs font-semibold transition border";
  const chipInactive =
    "bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100/90 dark:hover:bg-slate-700";
  const chipActive = "bg-primary/10 text-primary border-primary/70 shadow-sm";

  return (
    <div className="min-h-screen pt-[140px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Calendrier des événements"
          subtitle="Retrouvez l’ensemble des compétitions, animations, stages et regroupements départementaux. Filtrez par catégories d’âge, type, niveau et statut pour trouver rapidement ce qui vous intéresse."
          align="center"
          size="lg"
        />

        <InfoBubble title="Utiliser le calendrier">
          <p className="text-sm">
            Vous pouvez combiner plusieurs filtres en même temps : par exemple
            afficher uniquement les compétitions départementales Cadets &
            Juniors à venir. Cliquez ensuite sur un événement pour voir tous les
            détails (horaires, documents, lien agenda...).
          </p>
        </InfoBubble>

        {/* Carte filtres */}
        <div className="mt-6 mb-5 rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          {/* Bandeau haut */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

          <div className="p-4 md:p-5 space-y-4">
            {/* Ligne 1 : Recherche + Vue + Statut */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* Recherche */}
              <div className="w-full md:max-w-md">
                <label className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1.5 block">
                  Recherche par titre
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                  <input
                    type="text"
                    placeholder="Ex : Tournoi Cadets, Stage départemental..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                  />
                </div>
              </div>

              {/* Vue + Statut + reset */}
              <div className="flex flex-col items-stretch md:items-end gap-2">
                {/* Vue */}
                <div className="inline-flex items-center gap-2">
                  <span className="hidden md:inline text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                    Vue
                  </span>
                  <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1">
                    <button
                      type="button"
                      onClick={() => setViewMode("cards")}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition ${
                        viewMode === "cards"
                          ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                          : "text-slate-600 dark:text-slate-300"
                      }`}
                    >
                      <FaTh className="text-xs" />
                      <span className="hidden sm:inline">Cartes</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("table")}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition ${
                        viewMode === "table"
                          ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                          : "text-slate-600 dark:text-slate-300"
                      }`}
                    >
                      <FaList className="text-xs" />
                      <span className="hidden sm:inline">Tableau</span>
                    </button>
                  </div>
                </div>

                {/* Statut + reset */}
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <div className="inline-flex items-center gap-1">
                    <FaFilter className="text-[11px] text-slate-400" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                      Statut
                    </span>
                  </div>
                  <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setStatusFilter(status)}
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                          statusFilter === status
                            ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                            : "text-slate-600 dark:text-slate-300"
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300 hover:text-primary dark:hover:text-primary"
                  >
                    <FaUndo className="text-[10px]" />
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>

            {/* Ligne 2 : Catégories */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300">
                  Catégories d’âge
                </span>
                {selectedCategories.length > 0 && (
                  <span className="text-[10px] text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {selectedCategories.length} sélectionnée(s)
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cateEvents.map((cate) => {
                  const active = selectedCategories.includes(cate);
                  return (
                    <button
                      key={cate}
                      type="button"
                      onClick={() => toggleCategory(cate)}
                      className={`${chipBase} ${
                        active ? chipActive : chipInactive
                      }`}
                    >
                      {cate}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ligne 3 : Types */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300">
                  Types d’événements
                </span>
                {selectedTypes.length > 0 && (
                  <span className="text-[10px] text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {selectedTypes.length} sélectionné(s)
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {typeEvents.map((type) => {
                  const active = selectedTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleType(type)}
                      className={`${chipBase} ${
                        active ? chipActive : chipInactive
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ligne 4 : Niveaux */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300">
                  Niveau
                </span>
                {selectedLevels.length > 0 && (
                  <span className="text-[10px] text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {selectedLevels.length} sélectionné(s)
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {levelEvents.map((level) => {
                  const active = selectedLevels.includes(level);
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => toggleLevel(level)}
                      className={`${chipBase} ${
                        active ? chipActive : chipInactive
                      }`}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Résultats */}
        {filteredEvents.length === 0 ? (
          <div className="mt-8 text-center text-sm text-slate-500 dark:text-slate-300">
            Aucun événement ne correspond à vos filtres.
          </div>
        ) : viewMode === "cards" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filteredEvents.map((event) => (
              <EventCardUser
                key={event.id}
                event={event}
                userRole={userRole}
                onClick={() => handleEventClick(event)}
              />
            ))}
          </div>
        ) : (
          <EventTableUser
            events={filteredEvents}
            onEventClick={handleEventClick}
          />
        )}
      </div>

      <EventDetailsModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={closeModal}
        userRole={userRole}
      />
    </div>
  );
};

export default CalendarPage;
